import { useEffect, useMemo, useRef, useState } from 'react';
import { createClient, SubscribePayload } from 'graphql-ws';

import { PageDeleted, RefreshContentSearch, ScheduledPageCanceled } from 'data/generated/graphql';
import { useAuthToken } from 'hooks/useAuthToken';
import env from 'utils/env';

export type ReceivedData = {
  refreshContentSearch?: RefreshContentSearch;
  pageDeleted?: PageDeleted;
  scheduledPageCanceled?: ScheduledPageCanceled;
};

type SubscriptionMessage = {
  data: ReceivedData;
};

type ClosingEvent = {
  code: number;
  reason: string;
};

interface UseSubscriptionProps {
  subscribePayload: SubscribePayload;
  publicationKey: string;
  showLog?: boolean;
  retryAttempts?: number;
  enabled?: boolean;
}

const disabledLogger = {
  info: () => {},
  warn: () => {},
  error: () => {},
  log: () => {}
};

const loggerWrapper = (showLog: boolean = false) => {
  if (process.env.NODE_ENV === 'development' && showLog) {
    return {
      info: console.info.bind(console, '[useSubscription]'),
      warn: console.warn.bind(console, '[useSubscription]'),
      error: console.error.bind(console, '[useSubscription]'),
      // eslint-disable-next-line no-console
      log: console.log.bind(console, '[useSubscription]')
    };
  }

  return disabledLogger;
};

export const useSubscription = ({
  subscribePayload,
  publicationKey,
  showLog,
  retryAttempts = 5,
  enabled = true
}: UseSubscriptionProps) => {
  const authToken = useAuthToken();
  const [data, setData] = useState<ReceivedData | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const retryCountRef = useRef(0);
  const logger = useMemo(() => loggerWrapper(showLog), [showLog]);

  const client = useMemo(() => {
    if (!authToken || !enabled) {
      return null;
    }

    logger.info(`Creating new WebSocket client for subscription "${subscribePayload.query}"`);

    const apiUrl = new URL(env.API_URL);
    const wsUrl = new URL(apiUrl.toString());

    if (apiUrl.protocol === 'http:') {
      wsUrl.protocol = 'ws:';
    } else if (apiUrl.protocol === 'https:') {
      wsUrl.protocol = 'wss:';
    }

    wsUrl.pathname = `${wsUrl.pathname.replace(/\/$/, '')}/graphql`;

    const newClient = createClient({
      url: wsUrl.toString(),
      retryAttempts,
      keepAlive: 5000, // Send a ping every 5 seconds to keep the connection alive on Servo
      shouldRetry: () => {
        retryCountRef.current += 1;

        logger.warn(`Retry attempt ${retryCountRef.current} for subscription "${subscribePayload.query}"`);

        if (retryCountRef.current >= retryAttempts) {
          logger.error(`Max retry attempts reached for subscription "${subscribePayload.query}". Giving up.`);
          setError(new Error(`Max retry attempts reached for subscription "${subscribePayload.query}"`));
          retryCountRef.current = 0; // Reset retry count
          return false; // Stop retrying
        }

        return true; // Retry on error
      },
      connectionParams: {
        Authorization: authToken,
        'X-Publication-Key': publicationKey,
        'X-Client-Id': 'curation-ucs-frontend'
      }
    });

    newClient.on('closed', (event) => {
      const { code, reason } = event as ClosingEvent;
      logger.info(
        `WebSocket connection closed for subscription "${subscribePayload.query}" (code: ${code}, reason: ${reason})`
      );
    });

    newClient.on('connected', () => {
      logger.info(`WebSocket connection established for subscription "${subscribePayload.query}"`);
    });

    return newClient;
  }, [authToken, enabled, logger, publicationKey, retryAttempts, subscribePayload.query]);

  useEffect(() => {
    if (client && subscribePayload.query) {
      let disposeSubscription = () => {};

      logger.info(
        `Setting up subscription for "${subscribePayload.query}" with variables:`,
        subscribePayload.variables
      );

      try {
        disposeSubscription = client.subscribe(
          {
            query: subscribePayload.query,
            ...(subscribePayload.variables ? { variables: subscribePayload.variables } : {}),
            ...(subscribePayload.operationName ? { operationName: subscribePayload.operationName } : {}),
            ...(subscribePayload.extensions ? { extensions: subscribePayload.extensions } : {})
          },
          {
            next: (eventData) => {
              const { data } = eventData as SubscriptionMessage;

              setData(data);
            },
            error: (err) => {
              logger.error('Subscription error:', err);
              setError(err as Error);
            },
            complete: () => {
              logger.info('Subscription complete');
            }
          }
        );
      } catch (error) {
        logger.error('Error setting up subscription:', error);
        setError(error as Error);
      }

      return () => {
        disposeSubscription();
        void client.dispose();
      };
    }

    return () => {};
  }, [client, logger, subscribePayload]);

  return {
    data,
    error
  };
};
